import { type NavItem } from "@procureflow/shared-types";

// US-003c — Route guard. Maps URL path → the NavItem(s) a role must
// hold to access it. canAccessPath is the predicate AppLayout uses
// to decide between rendering the route and rendering <Forbidden403/>.
//
// A null return from requiredNavItemsForPath marks the path as PUBLIC
// (login, the 403 page itself, the root error pages). Public paths
// are reachable whether or not the user is signed in.
//
// An empty array means "denied by default" — the path is protected
// but no rule was registered. Surfaces routes the team forgot to
// register here, rather than silently allowing everything.

const PUBLIC_PREFIXES = ["/login", "/403"] as const;

// Order matters: most specific prefix first. Each entry tries to match
// the *segment* boundary so /vendors matches /vendors and /vendors/abc
// but not /vendors-list.
interface Rule {
  prefix: string;
  navItems: readonly NavItem[];
}

const RULES: readonly Rule[] = [
  // Specific sub-screens of /invoices that need their own line for
  // documentation purposes. They all collapse to the `invoices` key
  // since the manifest is screen-grained, not sub-page-grained.
  { prefix: "/invoices/payment-queue", navItems: ["invoices"] },
  { prefix: "/invoices/new", navItems: ["invoices"] },
  { prefix: "/invoices", navItems: ["invoices"] },

  // approval_queue added: US-014 approvers click "Review" in their
  // queue and land on /purchase-requests/$id. Without approval_queue
  // here the deep-link 403s for Finance / Procurement / Admin approvers.
  { prefix: "/purchase-requests", navItems: ["prs", "my_prs", "approval_queue"] },
  { prefix: "/approvals", navItems: ["approval_queue"] },
  { prefix: "/rfq", navItems: ["rfqs"] },
  { prefix: "/purchase-orders", navItems: ["purchase_orders"] },
  { prefix: "/grn", navItems: ["grns"] },
  // US-059 — item master catalogue (Admin / Procurement Officer).
  { prefix: "/item-master", navItems: ["item_master"] },
  { prefix: "/exceptions", navItems: ["invoices"] },
  { prefix: "/vendors", navItems: ["vendors"] },
  { prefix: "/reports", navItems: ["reports"] },
  { prefix: "/finance", navItems: ["reports"] },
  { prefix: "/notifications", navItems: ["notifications"] },
  { prefix: "/users", navItems: ["user_management"] },
  // US-050 — audit-trail page accepts Finance (reports) and Admin
  // (settings). Both navItems are equivalent gate conditions here.
  { prefix: "/audit-logs", navItems: ["settings", "reports"] },
  // US-012 — approval rules config is Admin-only.
  { prefix: "/approval-rules", navItems: ["settings"] },
  // US-037 — match policy: Admin edits, Finance + Procurement view.
  { prefix: "/settings/match-policy", navItems: ["match_policy"] },
  // US-061 — settings landing (functional currency). Admin-only (settings).
  { prefix: "/settings", navItems: ["settings"] },
  // US-054 — role management is Admin-only (settings navItem).
  { prefix: "/settings/roles", navItems: ["settings"] },
  // US-056 — per-document-type approval chain config is Admin-only.
  { prefix: "/settings/approvals", navItems: ["settings"] },
];

function pathSegmentMatches(path: string, prefix: string): boolean {
  if (path === prefix) return true;
  return path.startsWith(`${prefix}/`) || path.startsWith(`${prefix}?`);
}

export function requiredNavItemsForPath(path: string): readonly NavItem[] | null {
  // Strip the query string for prefix matching; pathSegmentMatches
  // accepts '?' separately for the search-suffix-on-prefix case.
  const pathOnly = path.split("?")[0];

  for (const pub of PUBLIC_PREFIXES) {
    if (pathOnly === pub || pathOnly.startsWith(`${pub}/`)) return null;
  }

  // Dashboard root — explicit because '/' isn't a prefix that matches
  // every other path.
  if (pathOnly === "/") return ["dashboard"];

  for (const rule of RULES) {
    if (pathSegmentMatches(pathOnly, rule.prefix)) return rule.navItems;
  }

  // Unknown protected path → deny by default. The 403 surface tells
  // the user to ask for access; better than silently rendering an
  // empty page that a future route registration would un-break.
  return [];
}

// ADR-017 §3 — gates on the user's resolved nav items (fetched from
// /api/me/permissions, with a static fallback) rather than a role, so a
// role edit takes effect on the next session without a code change.
export function canAccessPath(allowedNavItems: readonly NavItem[], path: string): boolean {
  const required = requiredNavItemsForPath(path);
  if (required === null) return true; // public
  if (required.length === 0) return false; // deny by default
  const allowed = new Set<NavItem>(allowedNavItems);
  return required.some((item) => allowed.has(item));
}
